import { Box, ButtonBase, createStyles, makeStyles } from '@material-ui/core'
import React, { useMemo } from 'react'
import Link from 'next/link'
import { Colors, NotificationBadge, Typography } from 'src/components/atoms'
import { DynamicMuiIcon, MuiIconVariant } from '../../atoms/DynamicMuiIcon'
import { useRouter } from 'next/router'
import { useChannelTalk } from 'src/fixtures/utils/channelTalk'

interface SPSideMenuItemType {
  path: string
  icon: MuiIconVariant
  label: string
  count?: number
  targetBlank?: boolean
  onClick?: () => void
}

const useStyles = makeStyles(() => {
  return createStyles({
    link: {
      textDecoration: 'none'
    },
    button: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 16px',
      color: Colors.base.middleGray,
      transition: '512ms',
      '&:hover': {
        background: Colors.background.gray,
        color: Colors.base.black
      }
    },
    activeButton: {
      background: Colors.background.gray,
      color: Colors.base.black
    },
    transition: {
      transition: '256ms'
    },
    bannerBtn: {
      display: 'block',
      width: '100%'
    }
  })
})

export interface SPSideMenuProps {
  inboxCount?: number
  notificationCount?: number
  isExpanded: boolean
}

export const EXPANDED_SIDE_MENU_SIZE = 181
export const MIN_SIDE_MENU_SIZE = 0

const isCurrentPage = (path: string, currentPage: string) => {
  if (path.split('/')[1] === 'setting') {
    return path.split('/')[2] === currentPage.split('/')[2]
  }

  if (path.split('/')[1] === '') {
    return path === currentPage
  }
  return path.split('/')[1] === currentPage.split('/')[1]
}

export const SPSideMenu = ({ inboxCount, isExpanded }: SPSideMenuProps) => {
  const router = useRouter()
  const { showMessenger } = useChannelTalk()
  const currentPage = router.pathname
  const classes = useStyles({ isExpanded })
  const SIDE_MENU_SIZE = useMemo(() => (isExpanded ? EXPANDED_SIDE_MENU_SIZE : MIN_SIDE_MENU_SIZE), [isExpanded])
  const SPSideMenuItemList: SPSideMenuItemType[] = useMemo(() => {
    return [
      { path: '/inbox', icon: 'inbox', label: '受信トレイ', count: inboxCount },
      { path: '/contacts', icon: 'account', label: '連絡先' },
      { path: '/companies', icon: 'business', label: '会社' },
      { path: '/documents', icon: 'document', label: '資料' },
      { path: '/multi-documents', icon: 'computer', label: '資料サイト' },
      { path: '/announcements', icon: 'notification', label: 'お知らせ' },
      {
        path: '#',
        icon: 'quiz',
        label: 'お問い合わせ',
        targetBlank: true,
        onClick: () => showMessenger()
      },
      { path: '/setting/user/info', icon: 'manageAccounts', label: '個人設定' },
      { path: '/setting/team/info', icon: 'groups', label: 'チーム設定' }
    ]
  }, [inboxCount, showMessenger])

  return (
    <Box
      width={SIDE_MENU_SIZE}
      height={1}
      display="flex"
      flexDirection="column"
      bgcolor={Colors.functional.background.default}
      overflow="hidden"
      className={classes.transition}
    >
      <Box
        borderRight={`1px solid ${Colors.background.silver}`}
        borderTop={`1px solid ${Colors.background.silver}`}
        height={1}
      >
        {isExpanded &&
          SPSideMenuItemList.map(({ label, path, icon, count }) => {
            return (
              <Link key={label} href={path} as={path} prefetch={false}>
                <a className={classes.link}>
                  <ButtonBase
                    className={`${classes.button} ${isCurrentPage(path, currentPage) && classes.activeButton}`}
                  >
                    <Box display="flex" alignItems="center">
                      <DynamicMuiIcon variant={icon} />
                      <Box ml="8px" whiteSpace="noWrap">
                        <Typography variant="h5">{label}</Typography>
                      </Box>
                    </Box>
                    {count && isExpanded && (
                      <Box width="31px">
                        <NotificationBadge count={count} />
                      </Box>
                    )}
                  </ButtonBase>
                </a>
              </Link>
            )
          })}
      </Box>
    </Box>
  )
}
